import React, { useState, useEffect, useRef } from 'react';
import { sbSendMessage, sbGetHistory, sbGetChatList, sbCreateNewChat } from 'utils/methods';
import Breadcrumbs from 'components/Breadcrumbs';
import { marked } from 'marked';
import katex from 'katex';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { useTranslation } from 'react-i18next';

import 'katex/dist/katex.min.css';

const SafetyBuddy = () => {
  const { t } = useTranslation();
  const [chatId, setChatId] = useState(localStorage.getItem("chat_id") || null);
  const [messages, setMessages] = useState([]);
  const [chatList, setChatList] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const chatContainerRef = useRef(null);

  const navigate = useNavigate();
  const { isAuthenticated, user, token } = useAuth();

  const fetchChatHistory = async () => {
    try {
      if (!chatId) return;
      const response = await sbGetHistory(chatId);
      const { data } = response;
      // data.history expected to be an array of messages
      setMessages(data.history || []);
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      console.error("Error fetching chat history:", error);
    }
  };

  const fetchChatList = async () => {
    try {
      setLoadingChats(true);
      const res = await sbGetChatList();
      const { data } = res;
      // Accept response shapes like { chats: [...] } or direct array
      const list = data && data.chats ? data.chats : (Array.isArray(data) ? data : (data.items || []));
      setChatList(list || []);
    } catch (err) {
      console.error('Error fetching chat list:', err);
      if (err.response?.status === 401) {
        console.error('Not authenticated - token may not be set');
      }
      // If error is due to missing column or auth, just set empty list
      setChatList([]);
    } finally {
      setLoadingChats(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;

    const userMessage = { role: "user", content: userInput };
    setMessages(prev => [...prev, userMessage]);

    // Clear input immediately
    setUserInput("");
    setLoading(true);
    
    try {
      const response = await sbSendMessage({ query: userInput, chat_id: chatId });
      const { data } = response;

      const newChatId = data.chat_id;
      setChatId(newChatId);
      localStorage.setItem("chat_id", newChatId);
      
      setMessages(prev => [...prev, {
        role: "assistant",
        content: data.response,
      }]);
      
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatMessage = (content) => {
    // Parse markdown
    let html = marked.parse(content || '');
    
    // Render LaTeX (inline $...$ and block $$...$$)
    html = html.replace(/\$\$([\s\S]+?)\$\$/g, (match, tex) => {
      try {
        return katex.renderToString(tex, { displayMode: true });
      } catch {
        return match;
      }
    });
    
    html = html.replace(/\$([^$\n]+)\$/g, (match, tex) => {
      try {
        return katex.renderToString(tex, { displayMode: false });
      } catch {
        return match;
      }
    });
    
    // Make all <a> tags open in a new tab
    html = html.replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');
    return html;
  };

  const handleChatScroll = () => {
    const container = chatContainerRef.current;
    if (!container) return;
    
    // Show button if not at bottom (with a small threshold)
    const threshold = 20;
    setShowScrollToBottom(
      container.scrollHeight - container.scrollTop - container.clientHeight > threshold
    );
  };

  const scrollToBottom = () => {
    const container = chatContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      setShowScrollToBottom(false);
    }
  };

  useEffect(() => {
    if (chatId) {
      fetchChatHistory();
    } else {
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [chatId]);

  useEffect(() => {
    // If new message is added and user is at the bottom, keep at bottom
    setTimeout(() => {
      const container = chatContainerRef.current;
      if (container) {
        const threshold = 20;
        const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
        if (atBottom) {
          scrollToBottom();
        }
      }
    }, 100);
  }, [messages]);

  useEffect(() => {
    // Scroll to bottom when loading state changes (thinking animation appears/disappears)
    if (loading) {
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [loading]);

  // Fetch chat list for authenticated users
  useEffect(() => {
    if (isAuthenticated && token) {
      // Small delay to ensure axios token is set
      const timer = setTimeout(() => {
        fetchChatList();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setChatList([]);
    }
  }, [isAuthenticated, token]);

  // Handler to start a new chat (server-side if possible)
  const handleNewChat = async () => {
    try {
      setLoading(true);
      // Try create on server first
      const res = await sbCreateNewChat();
      const { data } = res;
      const newId = data?.chat_id || data?.id || null;
      setChatId(newId);
      if (newId) localStorage.setItem('chat_id', newId);
      setMessages([]);
      // refresh list
      fetchChatList();
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      // Fallback to client-side new chat
      console.error('Error creating new chat on server, falling back to client-only:', error);
      setChatId(null);
      setMessages([]);
      localStorage.removeItem('chat_id');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChat = (chat) => {
    // chat may be an object or id - handle session_uuid from backend
    const id = chat?.session_uuid || chat?.chat_id || chat?.id || chat;
    if (!id) return;
    setChatId(id);
    localStorage.setItem('chat_id', id);
  };

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col pt-[10%] min-h-screen bg-gray-50">

        <div className="text-center py-8">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('safetyBuddy.authRequired')}</h3>
          <p className="text-gray-600 mb-4">{t('safetyBuddy.authRequiredDesc')}</p>
          <div className="space-x-4">
            <button 
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('safetyBuddy.logIn')}
            </button>
            <button 
              onClick={() => navigate('/register')}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {t('safetyBuddy.signUp')}
            </button>
          </div>
        </div>

      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">

      <div className="flex flex-col items-center justify-center my-6 gap-4">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 text-center">
          {t('safetyBuddy.title')}
        </h1>
        <div className="col-span-5">
          <Breadcrumbs />
        </div>
      </div>

      <div className="flex-1 max-w-5xl mx-auto w-full px-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Left: Chats list (hidden on small screens) */}
          <div className="hidden md:block w-72 bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">{t('safetyBuddy.chats')}</h3>
              <button onClick={handleNewChat} className="text-xs text-blue-600 hover:underline">{t('safetyBuddy.newChat')}</button>
            </div>
            <div className="space-y-2 overflow-y-auto" style={{ maxHeight: '520px' }}>
              {loadingChats ? (
                <div className="text-sm text-gray-500">{t('common.loading')}</div>
              ) : chatList && chatList.length ? (
                chatList.map((c, idx) => {
                  const id = c.chat_id || c.session_uuid || c.id || c;
                  // Generate title from session_uuid or created_at since title column doesn't exist
                  const displayTitle = c.session_uuid 
                    ? `Chat ${c.session_uuid.slice(0, 8)}...` 
                    : (c.created_at 
                      ? `Chat ${new Date(c.created_at).toLocaleDateString()}` 
                      : t('safetyBuddy.untitled'));
                  return (
                    <button key={id || idx} onClick={() => handleSelectChat(c)} className={`w-full text-left p-2 rounded-md hover:bg-gray-100 transition-colors ${(id === chatId || c.session_uuid === chatId) ? 'bg-blue-50' : ''}`}>
                      <div className="text-sm font-medium text-gray-800 truncate">{displayTitle}</div>
                      <div className="text-xs text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</div>
                    </button>
                  )
                })
              ) : (
                <div className="text-sm text-gray-500">{t('safetyBuddy.noChats')}</div>
              )}
            </div>
          </div>

          {/* Right: Chat area */}
          <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div
              ref={chatContainerRef}
              className="flex flex-col overflow-y-auto space-y-4 py-4 h-96 md:h-[500px]"
              onScroll={handleChatScroll}
              style={{ scrollBehavior: 'smooth' }}
            >
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex w-full ${
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div className={`flex max-w-[85%] ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'} gap-3`}>
                    
                    {/* Message Content */}
                    <div className={`px-4 py-3 rounded-lg w-full ${
                        message.role === 'user' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-transparent text-gray-800'
                      } chat-markdown`}
                      dangerouslySetInnerHTML={{ __html: formatMessage(message.content.trim()) }}
                    />
                  </div>
                </div>
              ))}

              {/* Thinking Animation */}
          {loading && (
            <div className="flex w-full justify-start">
              <div className="flex max-w-[85%] flex-row gap-3">
                {/* Avatar */}
                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold bg-green-600">
                  SB
                </div>
                
                {/* Thinking Animation */}
                <div className="px-4 py-3 rounded-lg bg-transparent text-gray-800">
                  <div className="flex items-center space-x-2">
                    <span className="text-gray-600">{t('safetyBuddy.thinking')}</span>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {showScrollToBottom && (
          <button
            onClick={scrollToBottom}
            className="fixed bottom-32 right-8 z-50 bg-gray-600 text-white rounded-full shadow-lg p-3 hover:bg-gray-700 transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
            </svg>
          </button>
        )}
        
        {/* Input Section */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4">
          <div className="max-w-5xl mx-auto">
            <form onSubmit={sendMessage} className="flex items-center space-x-3">
              <div className="flex relative w-full items-center">
                {/* <button
                  onClick={handleNewChat}
                  className="mx-2 px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition-colors text-sm"
                >
                  New chat
                </button> */}
                <textarea
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage(e);
                    }
                  }}
                  placeholder={t('safetyBuddy.messagePlaceholder')}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none max-h-32"
                  rows="1"
                  style={{ minHeight: '44px' }}
                />
              </div>
              <button
                type="submit"
                className="bg-blue-600 text-white p-3 rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || !userInput.trim()}
              >
                {loading ? (
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 1 1 16 0A8 8 0 0 1 4 12z"></path>
                  </svg>
                ) : (
                  <svg className="w-5 h-5 rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                  </svg>
                )}
              </button>
            </form>
            
            <div className='mt-5'>

              <button
                onClick={handleNewChat}
                className="mx-2 px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition-colors text-sm"
              >
                {t('safetyBuddy.newChat')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Chat markdown styles with forced inheritance */
        .chat-markdown ul {
          list-style-type: disc !important;
          margin-bottom: 1rem !important;
          padding-left: 1.5rem !important;
          display: block !important;
        }
        
        .chat-markdown ol {
          list-style-type: decimal !important;
          margin-bottom: 1rem !important;
          padding-left: 1.5rem !important;
          display: block !important;
        }
        
        .chat-markdown li {
          margin-bottom: 0.5rem !important;
          line-height: 1.5 !important;
          display: list-item !important;
          list-style-position: outside !important;
        }
        
        .chat-markdown p {
          margin-bottom: 1rem !important;
          line-height: 1.6 !important;
          display: block !important;
        }

        .chat-markdown h1 {
          font-size: 1.5rem !important;
          font-weight: bold !important;
          margin-bottom: 1rem !important;
          margin-top: 1.5rem !important;
          display: block !important;
          border-bottom: 2px solid currentColor !important;
          padding-bottom: 0.5rem !important;
        }
        
        .chat-markdown h2 {
          font-size: 1.25rem !important;
          font-weight: bold !important;
          margin-bottom: 0.75rem !important;
          margin-top: 1.25rem !important;
          display: block !important;
        }
        
        .chat-markdown h3 {
          font-size: 1.125rem !important;
          font-weight: bold !important;
          margin-bottom: 0.75rem !important;
          margin-top: 1rem !important;
          display: block !important;
        }
        
        .chat-markdown h4 {
          font-size: 1rem !important;
          font-weight: bold !important;
          margin-bottom: 0.5rem !important;
          margin-top: 0.75rem !important;
          display: block !important;
        }
        
        .chat-markdown h5 {
          font-size: 0.95rem !important;
          font-weight: bold !important;
          margin-bottom: 0.5rem !important;
          margin-top: 0.75rem !important;
          display: block !important;
        }
        
        .chat-markdown h6 {
          font-size: 0.9rem !important;
          font-weight: bold !important;
          margin-bottom: 0.5rem !important;
          margin-top: 0.75rem !important;
          display: block !important;
        }

        .chat-markdown a {
          color: #2563eb !important;
          text-decoration: underline !important;
        }

        .chat-markdown a:hover {
          color: #1d4ed8 !important;
        }

        .chat-markdown strong {
          font-weight: bold !important;
        }

        .chat-markdown em {
          font-style: italic !important;
        }

        .chat-markdown code {
          background-color: #f3f4f6 !important;
          color: #1f2937 !important;
          padding: 0.125rem 0.25rem !important;
          border-radius: 0.25rem !important;
          font-family: 'Courier New', Courier, monospace !important;
          font-size: 0.875rem !important;
        }

        .chat-markdown pre {
          background-color: #f3f4f6 !important;
          color: #1f2937 !important;
          padding: 1rem !important;
          border-radius: 0.5rem !important;
          overflow-x: auto !important;
          margin-bottom: 1rem !important;
        }

        .chat-markdown blockquote {
          border-left: 4px solid #d1d5db !important;
          padding-left: 1rem !important;
          margin: 1rem 0 !important;
          font-style: italic !important;
          color: #6b7280 !important;
        }

        .chat-markdown hr {
          border: none !important;
          border-top: 1px solid #e5e7eb !important;
          margin: 1.5rem 0 !important;
        }

        /* Nested lists */
        .chat-markdown ul ul,
        .chat-markdown ol ol,
        .chat-markdown ul ol,
        .chat-markdown ol ul {
          margin-top: 0.5rem !important;
          margin-bottom: 0.5rem !important;
        }

        /* First and last element margins */
        .chat-markdown > *:first-child {
          margin-top: 0 !important;
        }

        .chat-markdown > *:last-child {
          margin-bottom: 0 !important;
        }

        /* Blue background messages (user) - light link colors */
        .bg-blue-600 .chat-markdown a {
          color: #bfdbfe !important;
        }

        .bg-blue-600 .chat-markdown a:hover {
          color: #dbeafe !important;
        }

        /* Assistant messages with transparent background */
        .bg-transparent .chat-markdown h1,
        .bg-transparent .chat-markdown h2,
        .bg-transparent .chat-markdown h3,
        .bg-transparent .chat-markdown h4,
        .bg-transparent .chat-markdown h5,
        .bg-transparent .chat-markdown h6 {
          color: #1f2937 !important;
        }

        .bg-transparent .chat-markdown p {
          color: #374151 !important;
        }

        .bg-transparent .chat-markdown li {
          color: #374151 !important;
        }

        .bg-transparent .chat-markdown strong {
          color: #1f2937 !important;
        }
      `}</style>
        </div>
      </div>
    </div>

  );
};

export default SafetyBuddy;
